/**
 * Book-level views of the community's season reading, built from the chapter
 * list and per-chapter counts the home page already loads.
 */

/**
 * Collapse sorted chapter numbers into ranges: [1, 2, 3, 7, 9, 10] → "1–3, 7, 9–10".
 */
export function formatChapterRanges(numbers) {
  const sorted = [...numbers].sort((a, b) => a - b);
  const parts = [];
  let start = null;
  let previous = null;

  for (const number of sorted) {
    if (start === null) {
      start = number;
    } else if (number !== previous + 1) {
      parts.push(start === previous ? `${start}` : `${start}–${previous}`);
      start = number;
    }
    previous = number;
  }
  if (start !== null) {
    parts.push(start === previous ? `${start}` : `${start}–${previous}`);
  }
  return parts.join(', ');
}

/**
 * Group the still-needed chapters by book, keeping the biblical order they
 * arrive in.
 *
 * @returns {Array<{book: string, testament: string, chapters: number[], ranges: string}>}
 */
export function groupNeededByBook(chaptersNeeded = []) {
  const books = new Map();
  for (const chapter of chaptersNeeded) {
    if (!books.has(chapter.book)) {
      books.set(chapter.book, { book: chapter.book, testament: chapter.testament, chapters: [] });
    }
    books.get(chapter.book).chapters.push(chapter.chapter);
  }
  return Array.from(books.values()).map(entry => ({
    ...entry,
    ranges: formatChapterRanges(entry.chapters)
  }));
}

/**
 * How many times each book has been read all the way through: the fewest
 * reads of any one of its chapters. `chapters` is in canonical order, so the
 * books come out in order too.
 *
 * @returns {Array<{book: string, testament: string, totalChapters: number, completions: number}>}
 */
export function bookCompletions(chapters = [], chapterReadCounts = {}) {
  const books = new Map();
  for (const chapter of chapters) {
    const count = chapterReadCounts[chapter.id] || 0;
    const entry = books.get(chapter.book);
    if (!entry) {
      books.set(chapter.book, {
        book: chapter.book,
        testament: chapter.testament,
        totalChapters: 1,
        completions: count
      });
    } else {
      entry.totalChapters += 1;
      entry.completions = Math.min(entry.completions, count);
    }
  }
  return Array.from(books.values());
}

/**
 * The community's complete-Bible target. The season site goal matches the
 * season counts the meters use; the challenge goal is the fallback.
 */
export function bibleCompletionGoal(stats) {
  const candidates = [
    stats?.goals?.find(goal => goal.key === 'total_bible_completions'),
    stats?.challengeGoals?.find(goal => goal.key === 'bible_completions')
  ];
  for (const goal of candidates) {
    const target = Number(goal?.targetValue);
    if (Number.isFinite(target) && target > 0) return target;
  }
  return null;
}
